import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { SemanticPathService } from '@spartacus/core';
import { first, map, switchMap, tap } from 'rxjs/operators';
import { ActiveCartService, MultiCartService } from '@spartacus/cart/base/core';
import { Cart } from '@spartacus/cart/base/root';
import { DeliveryModeEnum } from '../enums/delivery-mode.enum';

@Injectable({
  providedIn: 'root'
})
export class CheckoutStep3Guard implements CanActivate {
  constructor(
    protected activeCartService: ActiveCartService,
    protected router: Router,
    protected semanticPathService: SemanticPathService,
    protected multiCartService: MultiCartService
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return combineLatest([this.activeCartService.getActiveCartId(), this.activeCartService.getLoading()]).pipe(
      first(([, isLoading]) => !isLoading),
      tap(([cartId]) => {
        if (cartId) {
          this.multiCartService.reloadCart(cartId);
        }
      }),
      switchMap(() => combineLatest([this.activeCartService.getActive(), this.activeCartService.getLoading()])),
      first(([, isLoading]) => !isLoading),
      map(([cart]) => {
        if (this.isEmptyCart(cart) || this.isAnonymous(cart)) {
          return this.router.parseUrl(this.semanticPathService.get('cart'));
        }
        if (this.isDeliveryInfoIncomplete(cart)) {
          return this.router.parseUrl(this.semanticPathService.get('checkoutDeliveryMode'));
        }
        return true;
      })
    );
  }

  private isEmptyCart(cart: Cart): boolean {
    return !cart || !cart['entryGroups'] || cart['entryGroups'].length === 0;
  }

  private isDeliveryInfoIncomplete(cart: Cart): boolean {
    return (
      this.hasNoDeliveryMode(cart) ||
      this.hasNoDeliveryAddress(cart) ||
      (this.isRelayPointMode(cart) && this.hasNoWithdrawalAddress(cart)) ||
      (this.isRelayPointMode(cart) && !cart['isRelayPointUserInfoFilled'])
    );
  }
  private isRelayPointMode(cart: Cart) {
    return cart.deliveryMode['type'] === DeliveryModeEnum.RELAYPOINT;
  }

  private hasNoDeliveryMode(cart: Cart): boolean {
    return !cart.deliveryMode;
  }

  private hasNoDeliveryAddress(cart: Cart): boolean {
    return !cart.deliveryAddress;
  }

  private hasNoWithdrawalAddress(cart: Cart): boolean {
    return !cart['depositWithdrawalAddress'];
  }

  private isAnonymous(cart: Cart): boolean {
    return cart?.user?.uid === 'anonymous';
  }
}
